import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import {
  Box,
  CircularProgress,
  Drawer,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import type { Dayjs } from "dayjs";
import type { Dispatch, ReactElement, SetStateAction } from "react";
import { lazy, memo, Suspense, useMemo } from "react";
import FlexBox from "../../layouts/Flexbox";
import type { SlotCriteriaResult } from "./slotCriteria";
import type { TimeSlotMeta } from "./utils";
import { getSlotValidationDisplayForIndex } from "./slotValidationDisplay";
import "./style.css";

const SlotValidationDetailBlocks = lazy(() => import("./SlotValidationDetailBlocks"));

const validationDetailFallback = (
  <Stack alignItems="center" justifyContent="center" minHeight={100} width="100%">
    <CircularProgress size={24} />
  </Stack>
);

export type SlotInvalidCriteriaTooltipProps = {
  criteria: SlotCriteriaResult;
  selectedDate: Dayjs;
  slots: TimeSlotMeta[];
  slotIndex: number;
  userRole: string;
  oppositeUserTimezone: string | null;
  children: ReactElement;
  tooltipSlotId: string;
  openTooltipSlotId: string | null;
  setOpenTooltipSlotId: Dispatch<SetStateAction<string | null>>;
  /** Desktop: MUI Tooltip. Non-desktop: bottom sheet with same content. */
  presentation?: "tooltip" | "drawer";
};

function SlotInvalidCriteriaTooltip({
  criteria,
  selectedDate,
  slots,
  slotIndex,
  userRole,
  oppositeUserTimezone,
  children,
  tooltipSlotId,
  openTooltipSlotId,
  setOpenTooltipSlotId,
  presentation = "tooltip",
}: SlotInvalidCriteriaTooltipProps) {
  const theme = useTheme();
  const open = openTooltipSlotId === tooltipSlotId;

  const display = useMemo(
    () =>
      getSlotValidationDisplayForIndex(
        selectedDate,
        slots,
        slotIndex,
        oppositeUserTimezone,
        userRole
      ),
    [selectedDate, slots, slotIndex, oppositeUserTimezone, userRole]
  );

  if (criteria.allValid) {
    return children;
  }

  const detailContent = display ? (
    <div className="slotModalTooltipContent">
      <Suspense fallback={validationDetailFallback}>
        <SlotValidationDetailBlocks
          display={display}
          userRole={userRole}
          oppositeUserTimezone={oppositeUserTimezone}
          presentation="tooltip"
          selectedDateLabel={selectedDate.format("ddd, MMM D, YYYY")}
        />
      </Suspense>
    </div>
  ) : (
    <Box p={1.5}>
      <Typography variant="bodyLarge-helveticaNeue" color="text.secondary">
        Unable to load validation for this slot.
      </Typography>
    </Box>
  );

  if (presentation === "drawer") {
    return (
      <>
        <Box
          component="span"
          onClick={() => setOpenTooltipSlotId(tooltipSlotId)}
          sx={{ display: "inline-flex" }}
        >
          {children}
        </Box>
        <Drawer
          anchor="bottom"
          open={open}
          onClose={() => setOpenTooltipSlotId(null)}
          ModalProps={{ keepMounted: false }}
          sx={{
            zIndex: theme.zIndex.modal + 2,
          }}
          PaperProps={{
            className: "slotModalTooltipPaper slotModalDrawerPaper",
            sx: {
              bgcolor: "background.paper",
              borderColor: "divider",
            },
          }}
        >
          <FlexBox
            alignItems="center"
            justifyContent="space-between"
            px={1.5}
            pt={1}
            pb={0.5}
            gap={1}
          >
            <Typography variant="body-helveticaNeue-bold" color="text.primary">
              Criteria
            </Typography>
            <IconButton
              size="small"
              aria-label="Close"
              onClick={() => setOpenTooltipSlotId(null)}
            >
              <CloseRoundedIcon fontSize="small" />
            </IconButton>
          </FlexBox>
          {detailContent}
        </Drawer>
      </>
    );
  }

  return (
    <Tooltip
      open={open}
      onOpen={() => setOpenTooltipSlotId(tooltipSlotId)}
      onClose={() => setOpenTooltipSlotId(null)}
      arrow
      placement="right"
      title={detailContent}
      slotProps={{
        tooltip: {
          className:
            "slotModalTooltipPaper slotModalTooltipSlotSurface slotModalCriteriaTooltipPaper",
          sx: {
            bgcolor: "background.paper",
            color: "text.primary",
            borderColor: "divider",
            "& .MuiTooltip-arrow": {
              color: "background.paper",
              fontSize: 20,
            },
            "& .MuiTooltip-arrow:before": {
              border: 1,
              borderStyle: "solid",
              borderColor: "divider",
            },
          },
        },
        popper: {
          modifiers: [
            {
              name: "preventOverflow",
              options: {
                padding: 8,
                altAxis: true,
              },
            },
          ],
        },
      }}
    >
      <span className="slotModalTooltipTrigger">{children}</span>
    </Tooltip>
  );
}

export default memo(SlotInvalidCriteriaTooltip);
